import { FunctionComponent } from "react";
import { useQuery } from "react-query";
import { listSources } from "pages/api-clients/integrations";
import { SourceCheck } from "pages/api/[orgId]/integrations-client/definitions";
import { GithubIntegrationStatus } from "pages/[orgId]/settings/GithubIntegrationStatus";
import { JiraIntegrationStatus } from "pages/[orgId]/settings/JiraIntegrationStatus";
import { GCalendarIntegrationStatus } from "pages/[orgId]/settings/OAuthGcalendarIntegrationStatus";
import { CircleCiIntegrationStatus } from "pages/[orgId]/settings/CircleCiIntegrationStatus";
import { SlackIntegrationStatus } from "pages/[orgId]/settings/SlackIntegrationStatus";
import { GithubIcon } from "src/components/GithubIcon";
import { JiraIcon } from "src/components/JiraIcon";
import { GcalIcon } from "src/components/GcalIcon";
import { CircleCiIcon } from "src/components/CircleCiIcon";
import { SlackIcon } from "src/components/SlackIcon";
import { useQueryToasterErrors } from "src/hooks/useToaster";
import { SnykIntegrationStatus } from "pages/[orgId]/settings/SnykIntegrationStatus";
import { SnykIcon } from "src/components/SnykIcon";
import { GitlabIntegrationStatus } from "pages/[orgId]/settings/GitlabIntegrationStatus";
import { GitlabIcon } from "src/components/GitlabIcon";

export enum Integrations {
  GITHUB = "github",
  JIRA = "jira",
  GCALENDAR = "gcalendar",
  CIRCLECI = "circleci",
  SLACK = "slack",
  SNYK = "snyk",
  GITLAB = "gitlab",
}

// static description of available integrations.
// id maps to the id field in SourceCheck
export const INTEGRATIONS = [
  {
    id: Integrations.GITHUB,
    name: "Github",
    Icon: GithubIcon,
    Component: GithubIntegrationStatus,
    tags: ["source control", "issues", "deployments"], // should map to 'roles'
  },
  {
    id: Integrations.JIRA,
    name: "Jira",
    Icon: JiraIcon,
    Component: JiraIntegrationStatus,
    tags: ["issues"],
  },
  {
    id: Integrations.GCALENDAR,
    name: "Google Calendar",
    Icon: GcalIcon,
    Component: GCalendarIntegrationStatus,
    tags: ["meetings"],
  },
  {
    id: Integrations.CIRCLECI,
    name: "Circle CI",
    Icon: CircleCiIcon,
    Component: CircleCiIntegrationStatus,
    tags: ["deployments"],
  },
  {
    id: Integrations.SLACK,
    name: "Slack",
    Icon: SlackIcon,
    Component: SlackIntegrationStatus,
    tags: ["messages"],
  },
  {
    id: Integrations.SNYK,
    name: "Snyk",
    Icon: SnykIcon,
    Component: SnykIntegrationStatus,
    tags: ["analysis"],
  },
  {
    id: Integrations.GITLAB,
    name: "Gitlab",
    Icon: GitlabIcon,
    Component: GitlabIntegrationStatus,
    tags: ["source control", "issues", "deployments"],
  },
] as {
  id: Integrations;
  name: string;
  tags: string[];
  Icon: FunctionComponent<{ size?: number }>;
  Component: FunctionComponent;
  status?: "success" | "error" | "warn";
  check?: SourceCheck;
}[];

// const roleToTag = {
//   cicd: "deployments",
//   vcs: "source control",
//   project: "issues",
//   messaging: "messages",
//   calendar: "meetings",
//   securityScan: "analysis",
// };

export type Integration = (typeof INTEGRATIONS)[number];

export const useIntegrations = ({ orgId }: { orgId?: string }) => {
  const sources = useQuery(
    ["settings/integrations/list-sources"],
    async () => {
      const integrations = [...INTEGRATIONS];
      (await listSources(orgId!)).data.forEach((check) => {
        const integration = integrations.find(({ id }) => id === check.id);
        if (integration) {
          integration.check = check;
          if (check.erroredAt.length > 0) integration.status = "error";
          else if (Object.keys(check.latestHits).length > 0)
            integration.status = "success";
          else integration.status = "warn";
        }
      });
      return integrations;
    },
    { enabled: Boolean(orgId) }
  );
  useQueryToasterErrors(sources);
  const hasCicd = sources.data?.some(
    ({ id, status }) => id === Integrations.CIRCLECI && status === "success"
  );
  const hasIssues = sources.data?.some(
    ({ id, status }) => id === Integrations.JIRA && status === "success"
  );
  const hasSourceControl = sources.data?.some(
    ({ id, status }) => id === Integrations.GITHUB && status === "success"
  );
  const hasCalendar = sources.data?.some(
    ({ id, status }) => id === Integrations.GCALENDAR && status === "success"
  );
  const hasMessages = sources.data?.some(
    ({ id, status }) => id === Integrations.SLACK && status === "success"
  );
  return {
    isLoading: sources.isLoading,
    data: sources.data || [],
    hasCalendar,
    hasCicd,
    hasIssues,
    hasMessages,
    hasSourceControl,
  };
};
